// Stat-Bar count-up enhancement. The Fluid/React markup always ships the
// final value (SSR parity and no-JS fallback); this script only animates
// the visual transition from 0 once a stat scrolls into view. Only cells
// whose accent <em> carries an explicit numeric data-value take part,
// values like "24/7" or "18+" are never rewritten. Respects
// prefers-reduced-motion by leaving the SSR end value untouched.
const numberFormat = new Intl.NumberFormat('de-AT');

function parseValue(raw: string): number {
  // de-AT notation: "." groups thousands, "," is the decimal separator
  return parseFloat(raw.replace(/\./g, '').replace(',', '.'));
}

function decimalPlaces(raw: string): number {
  const commaIndex = raw.indexOf(',');
  return commaIndex === -1 ? 0 : raw.length - commaIndex - 1;
}

function durationMs(el: Element): number {
  const raw = getComputedStyle(el).getPropertyValue('--th-dur-slow').trim();
  const parsed = parseFloat(raw);
  const base = Number.isFinite(parsed) ? parsed : 400;
  return base * 3;
}

function animateStat(numberEl: HTMLElement, target: number, places: number, duration: number): void {
  const start = performance.now();

  function step(now: number): void {
    const progress = Math.min((now - start) / duration, 1);
    const current = Number((target * progress).toFixed(places));
    numberEl.textContent = numberFormat.format(current);
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

function initCountUp(): void {
  const stats = document.querySelectorAll<HTMLElement>('[data-th-countup]');
  if (stats.length === 0) {
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }
        const numberEl = entry.target.querySelector<HTMLElement>('.th-stat__num em[data-value]');
        const raw = numberEl?.dataset.value;
        if (numberEl && raw) {
          const target = parseValue(raw);
          if (Number.isFinite(target)) {
            animateStat(numberEl, target, decimalPlaces(raw), durationMs(entry.target));
          }
        }
        obs.unobserve(entry.target);
      });
    },
    { threshold: 0.4 },
  );

  stats.forEach((stat) => observer.observe(stat));
}

document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initCountUp) : initCountUp();
